"use client";

import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { parseISO } from "date-fns";
import { Text } from "@lmn/design-system";
import Accordion from "@/components/UI/Accordion/Accordion";
import { PriceCurveDataItem } from "@/lib/Types/PriceCurveResponse/PriceCurveResponse";
import { formatPrice } from "@/lib/Helpers/formatPrice";
import IconArrowLeft from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowLeft.js";
import IconCross from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCross.js";
import HoverToolTip from "@/components/UI/Tooltip/HooverTooltip";
import {
  AirportCode,
  Bar,
  BarColumn,
  BarInner,
  BarsWrapper,
  BarWrapper,
  CalendarContainer,
  Content,
  DateLabel,
  DismissButton,
  Header,
  NavButton,
  PriceLabel,
  TooltipContent,
  TooltipDetails,
  TooltipPrice,
  UnavailableBar,
  Wrapper,
} from "./PriceCurveStyles";
import PriceCurveSkeleton from "./PriceCurveSkeleton";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { StartrackingEventsNames } from "@/lib/startrack/trackingEvents";
import useTracking from "@/lib/startrack/hooks/useTracking";
import { useHotelInfoStore } from "@/lib/stores/hotelInfoStore";
import { usePriceCurve } from "@/app/api/usePriceCurve";
import { useDurations } from "@/app/api/useAirlines";
import { useDurationTabPrice } from "@/app/api/useDurationTabPrice";
import DurationTabs from "./DurationTabs";
import DurationTabsSkeleton from "./DurationTabsSkeleton";
import { AppConfig } from "@/lib/Types/AppConfig";
import { getFreshClickId } from "@/lib/Helpers/getFreshClickId";

// Helper to update URL via pushState (instant, no navigation delay)
const updateUrl = (newParams: URLSearchParams) => {
  const newUrl = `${window.location.pathname}?${newParams.toString()}`;
  window.history.pushState({}, "", newUrl);
};

function formatDateInfo(dateStr: string) {
  const date = parseISO(dateStr);
  const dayName = date.toLocaleDateString("de-DE", { weekday: "short" });
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("de-DE", { month: "short" });
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  return { dayName, day, month, isWeekend };
}

function formatSelectionDate(dateStr: string) {
  const date = parseISO(dateStr);
  return date.toLocaleDateString("de-DE");
}

const MIN_BAR_HEIGHT = 60;
const MAX_BAR_HEIGHT = 150;

interface PriceCurveClientProps {
  appConfig: AppConfig;
}

export default function PriceCurveClient({
  appConfig,
}: PriceCurveClientProps) {
  const params = useReactiveSearchParams();
  // Active tab = the page-wide search `duration` from the URL. Clicking a tab
  // updates this URL param so the calendar AND the offer list refetch for it.
  const activeDuration = Number(params.get("duration")) || 0;

  const {
    data: priceCurveData = [],
    isLoading,
    isFetching,
    error,
    enabled,
  } = usePriceCurve(appConfig);

  const { data: availableDurations, isLoading: durationsLoading } =
    useDurations(appConfig);
  const { price: activePrice, isLoading: activePriceLoading } =
    useDurationTabPrice(appConfig, activeDuration);
  // Reserve the tab-row height while durations load so the real tabs swap in
  // place (no CLS). Only when a search duration exists (PACKAGE flow).
  const showDurationTabsSkeleton = activeDuration > 0 && durationsLoading;
  const hasDurationTabs = activeDuration > 0 && availableDurations.length >= 2;

  const { track } = useTracking(appConfig);
  const hotelCid = useHotelInfoStore((state) => state.hotelInfo.hotelCid);
  // Initialize selectedDate from URL parameter
  const [selectedDate, setSelectedDate] = useState<string | null>(() =>
    params.get("priceCurveFrom")
  );
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isFirstLoad = useRef(true);
  const barRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const isLoadingData =
    isLoading || (isFetching && priceCurveData.length === 0);

  // Sync selectedDate when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setSelectedDate(params.get("priceCurveFrom"));
  }, [params]);

  const cheapestItem = useMemo(() => {
    return priceCurveData.reduce<PriceCurveDataItem | null>((min, item) => {
      if (item.cheapestPrice === null) return min;
      if (min?.cheapestPrice == null) return item;
      return item.cheapestPrice < min.cheapestPrice ? item : min;
    }, null);
  }, [priceCurveData]);

  const { minPrice, maxPrice } = useMemo(() => {
    const prices = priceCurveData
      .map((d) => d.cheapestPrice)
      .filter((p): p is number => p !== null);
    if (prices.length === 0) return { minPrice: 0, maxPrice: 0 };
    return {
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
    };
  }, [priceCurveData]);

  const getBarHeight = useCallback(
    (price: number | null): number => {
      if (price === null) return MIN_BAR_HEIGHT;
      if (maxPrice === minPrice) return (MIN_BAR_HEIGHT + MAX_BAR_HEIGHT) / 2;
      const normalized = (price - minPrice) / (maxPrice - minPrice);
      return MIN_BAR_HEIGHT + normalized * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT);
    },
    [minPrice, maxPrice]
  );

  const updateScrollButtons = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollByAmount = useCallback(
    (direction: "left" | "right") => {
      if (!scrollRef.current) return;
      const scrollAmount = 350;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });

      track(
        StartrackingEventsNames.ProductDetailPriceCalendarScrollingInteraction,
        {
          productId: hotelCid,
          searchId: params.get("searchId") ?? "",
          clickId: getFreshClickId(),
        }
      );
    },
    [hotelCid, track, params]
  );

  // Scroll to selected/cheapest bar when data loads
  useEffect(() => {
    if (priceCurveData.length === 0) return;

    const initialDate =
      params.get("priceCurveFrom") ?? cheapestItem?.date;
    if (!initialDate) return;

    const barEl = barRefs.current.get(initialDate);
    if (barEl && scrollRef.current) {
      const container = scrollRef.current;
      const scrollLeft =
        barEl.offsetLeft -
        container.offsetLeft -
        container.clientWidth / 2 +
        barEl.offsetWidth / 2;
      container.scrollTo({
        left: scrollLeft,
        behavior: isFirstLoad.current ? "smooth" : "instant",
      });
    }
    updateScrollButtons();
    isFirstLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceCurveData.length]);

  const handleBarClick = (item: PriceCurveDataItem) => {
    if (item.cheapestPrice === null) return;
    setSelectedDate(item.date);

    // Update URL with priceCurveFrom parameter
    const current = new URLSearchParams(params.toString());
    current.set("priceCurveFrom", item.date);
    // Preserve live clickId — params snapshot may contain a stale value
    const liveClickId = getFreshClickId();
    if (liveClickId) current.set("clickId", liveClickId);
    updateUrl(current);

    // track
    track(StartrackingEventsNames.ProductDetailPriceCalendarClicked, {
      productId: hotelCid,
      searchId: params.get("searchId") ?? "",
      clickId: getFreshClickId(),
      totalPrice: item?.cheapestPrice ?? 0,
      departureDate: item?.date ?? "",
      departureAirport: item?.departureAirport ?? "",
    });
  };

  const handleDurationSelect = (selected: number) => {
    // Page-wide duration change: update the `duration` URL param so BOTH the
    // calendar (usePriceCurve) and the offer list (useDateListSearch) refetch
    // for the new stay length. Same pushState + "urlchange" mechanism as
    // handleBarClick — no full page reload, no new searchId.
    // NOTE: a dedicated tab-switch tracking event is deferred until the
    // analytics/SAAB schema for this widget is defined.
    const current = new URLSearchParams(params.toString());
    current.set("duration", String(selected));
    const liveClickId = getFreshClickId();
    if (liveClickId) current.set("clickId", liveClickId);
    updateUrl(current);
  };

  const handleClearSelection = () => {
    setSelectedDate(null);

    // Remove priceCurveFrom parameter from URL
    const current = new URLSearchParams(params.toString());
    current.delete("priceCurveFrom");
    // Preserve live clickId — params snapshot may contain a stale value
    const liveClickId = getFreshClickId();
    if (liveClickId) current.set("clickId", liveClickId);
    updateUrl(current);
  };

  const selectedItem = useMemo(() => {
    if (!selectedDate) return null;
    return priceCurveData.find((item) => item.date === selectedDate) || null;
  }, [selectedDate, priceCurveData]);

  if (!enabled) return null;

  const hasData = !error && priceCurveData.length > 0;

  if (!isLoadingData && !hasData) return null;

  const trackAccordionCollapsing = (isOpened: boolean) => {
    track(StartrackingEventsNames.ProductDetailPriceCalendarOpenInteraction, {
      productId: hotelCid,
      searchId: params.get("searchId") ?? "",
      clickId: getFreshClickId(),
      action: isOpened ? "UNCOLLAPSE" : "COLLAPSE",
    });
  };

  return (
    <Wrapper data-testid="hotel-price-curve">
      <Accordion
        trigger={
          <Header>
            <Text variant="Title4" margin={0} weight="bold">
              Sparreisekalender
            </Text>
            <Text variant="BodySmall" margin={0} color="textSecondary">
              Entdecken Sie preisgünstigere Optionen, indem Sie Ihre Reisedaten
              flexibel anpassen.
            </Text>
          </Header>
        }
        triggerPosition="top"
        withChevronRight
        isOpenByDefault
        onToggle={trackAccordionCollapsing}
      >
        {showDurationTabsSkeleton ? (
          <DurationTabsSkeleton />
        ) : hasDurationTabs ? (
          <DurationTabs
            appConfig={appConfig}
            durations={availableDurations}
            activeDuration={activeDuration}
            activePrice={activePrice}
            activePriceLoading={activePriceLoading}
            onSelect={handleDurationSelect}
          />
        ) : null}
        {isLoadingData ? (
          <PriceCurveSkeleton
            selectedDate={selectedDate}
            onClearSelection={handleClearSelection}
          />
        ) : (
        <Content>
          <CalendarContainer>
            <NavButton
              onClick={() => scrollByAmount("left")}
              $disabled={!canScrollLeft}
              aria-label="Vorherige Tage"
            >
              <IconArrowLeft />
            </NavButton>

            <BarsWrapper
              ref={scrollRef}
              onScroll={updateScrollButtons}
              role="listbox"
              aria-label="Reisedaten Auswahl"
            >
              {priceCurveData.map((item) => {
                const dateInfo = formatDateInfo(item.date);
                const isSelected = selectedDate === item.date;
                const isCheapest = item === cheapestItem;
                const isUnavailable = item.cheapestPrice === null;

                return (
                  <BarColumn
                    key={item.date}
                    ref={(el) => {
                      if (el) barRefs.current.set(item.date, el);
                    }}
                    onClick={() => handleBarClick(item)}
                    $isUnavailable={isUnavailable}
                    data-testid={`price-curve-bar-${item.date}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isUnavailable}
                    aria-label={
                      isUnavailable || item.cheapestPrice === null
                        ? `${dateInfo.dayName} ${dateInfo.day} ${dateInfo.month}, nicht verfügbar`
                        : `${dateInfo.dayName} ${dateInfo.day} ${dateInfo.month
                        }, ${formatPrice(item.cheapestPrice)}${isCheapest ? ", günstigster Preis" : ""
                        }`
                    }
                    tabIndex={isUnavailable ? -1 : 0}
                  >
                    {!isUnavailable && item.cheapestPrice !== null ? (
                      <BarWrapper>
                        <HoverToolTip
                          placement="top"
                          content={
                            <TooltipContent>
                              <TooltipPrice>
                                ab {formatPrice(item.cheapestPrice)}
                              </TooltipPrice>
                              <TooltipDetails>
                                {item.departureAirportName},{" "}
                                {activeDuration || item.duration} Nächte
                              </TooltipDetails>
                            </TooltipContent>
                          }
                          trigger={
                            <BarInner>
                              <Bar
                                $height={getBarHeight(item.cheapestPrice)}
                                $isCheapest={isCheapest}
                                $isSelected={isSelected}
                              >
                                <PriceLabel
                                  variant="BodySmall"
                                  margin={0}
                                  $isSelected={isSelected}
                                >
                                  {formatPrice(item.cheapestPrice)}
                                </PriceLabel>
                                {item.departureAirport && (
                                  <AirportCode $isSelected={isSelected}>
                                    {item.departureAirport}
                                  </AirportCode>
                                )}
                              </Bar>
                            </BarInner>
                          }
                        />
                      </BarWrapper>
                    ) : (
                      <BarWrapper>
                        <HoverToolTip
                          placement="top"
                          content={
                            <TooltipContent>
                              <TooltipDetails>
                                keine Angebote verfügbar
                              </TooltipDetails>
                            </TooltipContent>
                          }
                          trigger={
                            <UnavailableBar>
                              <IconCross />
                            </UnavailableBar>
                          }
                        />
                      </BarWrapper>
                    )}

                    <DateLabel $isWeekend={dateInfo.isWeekend}>
                      <span>{dateInfo.dayName}</span>
                      <span>
                        {dateInfo.day} {dateInfo.month}
                      </span>
                    </DateLabel>
                  </BarColumn>
                );
              })}
            </BarsWrapper>

            <NavButton
              onClick={() => scrollByAmount("right")}
              $disabled={!canScrollRight}
              aria-label="Nächste Tage"
            >
              <IconArrowLeft style={{ transform: "rotate(180deg)" }} />
            </NavButton>
          </CalendarContainer>

          {selectedItem && selectedDate && (
            <DismissButton
              variant="secondary"
              appearance="outlined"
              iconTrailing={IconCross}
              iconSize={14}
              aria-label="Auswahl entfernen"
              onClick={handleClearSelection}
            >
              Hinreise: {formatSelectionDate(selectedDate)}
            </DismissButton>
          )}
        </Content>
        )}
      </Accordion>
    </Wrapper>
  );
}
